// shared-lib/cabinet-runs/cabinetFrontLayout.js
const {
  STRIP_END_INSET_M,
  STRIP_FRONT_INSET_M,
} = require('../under-cabinet-lighting');
const {
  CABINET,
  CABINET_HARDWARE,
  FLOATING_SHELF,
  floatingShelfCount,
} = require('./cabinetFrontSpecs');
const {
  DRAWN_DOOR_STYLE_IDS,
  DEFAULT_DOOR_STYLE,
  resolveDoorStyle,
} = require('./doorStyles');
const {
  MICROWAVE_CABINETS,
  microwaveOpening,
  microwaveShelfDepthM,
} = require('./microwaveCabinets');
const { WALL_DRAWERS } = require('./wallDrawers');
const { RACKS, rackPieces } = require('./racks');
const { glassHolderRails, GLASS_HOLDER } = require('./glassHolders');
const {
  angledEndCut,
  angledEndLocalOutline,
  angledEndInset,
} = require('./angledEnds');

const IN = 0.0254;

/**
 * The ONE derivation of a box cabinet's front: where the carcass, the toe
 * kick and every door / drawer / rail / apron / filler front sits, and where
 * each front's hardware goes. The web app's `cabinetRenderer.js` turns this
 * into meshes and `elevations/schematics/CabinetElevation.jsx` into an SVG
 * line drawing, so the two surfaces cannot disagree about a single front — and
 * the estimate counts the pulls it bills off the same fronts
 * (`cabinetHardware.js`), which is why it lives in shared-lib. The web path
 * (`proceduralRenderers/cabinetFrontLayout.js`) re-exports it.
 *
 * Coordinates are the renderer's: metres, origin at the object centre, +y up,
 * +z toward the room (the door face is at z = +depth/2). The schematic maps
 * them into its top-left-origin viewBox with `frontRect` / `sideRect`.
 *
 * Column vocabulary (`options.columns`, from `vanity-assembly`,
 * `kitchen-assembly/sinkSeat` and `cabinet-runs/bayOverrides`):
 *   - no columns       → uniform full-height doors across the width (this is
 *                        what every kitchen base from `buildLowerCabinet`
 *                        and every wall cabinet gets)
 *   - 'doors'          → a FALSE-DRAWER RAIL (no handle) over the doors
 *   - 'drawer-doors'   → one WORKING drawer (with a handle) over the doors
 *   - 'sink-front'     → a fixed sink APRON (no handle) over the doors; with
 *                        `sideDrawerWidth`, a working drawer that wide each
 *                        side of the apron, the doors still running under
 *                        all three (a vendor's sink cabinet as sold —
 *                        `cabinet-runs/sinkUnits`)
 *                        (those three top fronts and the first drawer of a
 *                        'drawers' stack are all one height — see
 *                        `cabinetTopFrontHeight`)
 *   - 'farmhouse-front'→ no strip; the doors start `apronDrop` below the top
 *                        because the sink's own apron stands in that plane
 *   - 'drawers'        → a stack of drawers: `drawerCount` of them, else ~8"
 *                        each; `graduated` puts a short one on top — the
 *                        shared top front — over equal ones (a vendor's
 *                        drawer base as sold, `cabinet-runs/fillRun`)
 *   - 'full-height-doors' → doors over the column's WHOLE height, no strip
 *                        above them — what a column-less box gets, for a
 *                        column that has a neighbour. A blind corner wall
 *                        cabinet is a 'false-panel' (the part the other run
 *                        stands across) beside one of these, and says which
 *                        edge its pull goes on (`handleOnRight`): the one
 *                        away from the buried part — and that it is ONE door
 *                        (`doors: 1`) whatever its width
 *                        (`cabinet-runs/upperTier`)
 *   - any of the door columns above may say how many doors it has
 *                        (`doors`); otherwise its width decides
 *                        (`cabinetDoorCount`)
 *   - 'false-panel'    → one blank slab, always flat, no handle
 *   - 'open-shelf'     → NO fronts: the box with no doors on it. What shows is
 *                        the shell's own edges and the shelves between them,
 *                        laid out as `openShelf.boards` (whole box only)
 *
 * This module is PURE (no Three.js, no React, no environment).
 */

// The drawn styles live in shared-lib (`cabinet-runs/doorStyles`), so the
// renderer, the panel and a family's door designs read one list.
const CABINET_DOOR_STYLES = DRAWN_DOOR_STYLE_IDS;
const CABINET_HARDWARE_TYPES = ['bar-pull', 'knob', 'cup-pull', 'handleless'];
const DEFAULT_HARDWARE_TYPE = 'bar-pull';
// Unknown / missing door styles render flat (`resolveDoorStyle`), so legacy
// data keeps its look.

/**
 * The style ONE front wears. Blank strips (rails, aprons, fillers, false
 * panels) are never styled. Glass is a run-upper DOOR treatment: on a
 * `glass-front` cabinet the drawers, the base doors, the tall doors and the
 * over-fridge cabinet's doors wear the same frame with a solid panel (shaker)
 * instead. Every surface that draws a front (3D, elevation, thumbnail, the
 * see-through test) asks here.
 */
function frontDoorStyle(layout, front, doorStyle) {
  if (!front.styled) return 'flat';
  const glassAllowed =
    layout.isUpperCabinet && !layout.isOverFridge && front.kind === 'door';
  if (doorStyle === 'glass-front' && !glassAllowed) return 'shaker';
  return doorStyle;
}

/**
 * The style a CORNER cabinet's one door wears — the run's style, under the
 * same rule as `frontDoorStyle`: glass is an upper-door treatment, so a lower
 * corner unit in a glass-front run wears the frame with a solid panel. The
 * 3D corner renderer and the corner elevation schematic both ask here.
 */
function cornerDoorStyle(doorStyle, isUpper) {
  const style = resolveDoorStyle(doorStyle);
  return style === 'glass-front' && !isUpper ? 'shaker' : style;
}

/** Unknown / missing hardware types render as a bar pull. */
function resolveHardwareType(type) {
  return CABINET_HARDWARE_TYPES.includes(type) ? type : DEFAULT_HARDWARE_TYPE;
}

/**
 * Rail / stile width of a framed (shaker / slim-shaker / raised-panel / glass)
 * front. Never lets the frame collapse the center panel — capped at a quarter
 * of the smaller front dimension, so tiny fronts degrade gracefully to a still-
 * recognizable shaker look. Shared by the mesh frame and the drawn inset. A
 * slim shaker's thin edge is the same width on a door and a drawer.
 */
function cabinetFrameRailWidth(width, height, isDrawer = false, style = null) {
  let railWidth = isDrawer ? CABINET.drawerRailWidth : CABINET.railWidth;
  if (style === 'slim-shaker') railWidth = CABINET.slimRailWidth;
  return Math.min(railWidth, Math.min(width, height) * CABINET.railMaxFrac);
}

/**
 * A MICROWAVE cabinet's face (`microwaveCabinets.js`, group 4): a base one's
 * drawer under its opening, a wall one's doors over it — the opening itself
 * left with no front, the microwave the run places standing in it.
 * `microwave.opening` is the space on the face; `shelfDepth` how deep a wall
 * one's microwave shelf is, standing out past the box (null in a base).
 */
function microwaveFronts({
  frontId,
  width,
  bodyCenterY,
  doorHeight,
  handleAtBottom,
}) {
  const { doorReveal, doorGap } = CABINET;
  const doorTop = bodyCenterY + doorHeight / 2;
  const doorBottom = bodyCenterY - doorHeight / 2;
  const opening = microwaveOpening(frontId, { doorTop, doorBottom });
  const left = -width / 2 + doorReveal;
  const spanWidth = width - doorReveal * 2;
  const base = MICROWAVE_CABINETS[frontId].tier === 'base';
  let fronts;
  if (base) {
    const height = opening.bottomY - doorGap - doorBottom;
    fronts = drawerStack({
      column: { type: 'drawers', drawerCount: 1 },
      columnIndex: 0,
      left,
      colWidth: spanWidth,
      top: doorBottom + height,
      doorHeight: height,
    });
  } else {
    const height = doorTop - (opening.topY + doorGap);
    fronts = doorBank({
      left,
      spanWidth,
      centerY: doorTop - height / 2,
      height,
      handleAtBottom,
      singleHandleOnRight: true,
      firstIndex: 0,
    });
  }
  return {
    fronts: fronts.map((f) => ({ ...f, column: 0 })),
    spans: [{ index: 0, type: frontId, left: -width / 2, right: width / 2 }],
    microwave: {
      opening: {
        x: 0,
        y: (opening.topY + opening.bottomY) / 2,
        width: spanWidth,
        height: opening.topY - opening.bottomY,
      },
      shelfDepth: base ? null : microwaveShelfDepthM(),
    },
  };
}

/**
 * A DOOR OVER DRAWERS (`wallDrawers.js`, group 4; Venus WD18xx): the
 * column's `drawerCount` drawers at the bottom, each `WALL_DRAWERS.drawerIn`
 * tall, and the door bank over them.
 */
function doorOverDrawerFronts({
  column,
  width,
  bodyCenterY,
  doorHeight,
  handleAtBottom,
}) {
  const { doorReveal, doorGap } = CABINET;
  const doorTop = bodyCenterY + doorHeight / 2;
  const doorBottom = bodyCenterY - doorHeight / 2;
  const count = column.drawerCount ?? WALL_DRAWERS.drawers;
  const stack = count * WALL_DRAWERS.drawerIn * IN - doorReveal;
  const left = -width / 2 + doorReveal;
  const spanWidth = width - doorReveal * 2;
  const drawers = drawerStack({
    column: { type: 'drawers', drawerCount: count },
    columnIndex: 0,
    left,
    colWidth: spanWidth,
    top: doorBottom + stack,
    doorHeight: stack,
  });
  const height = doorTop - (doorBottom + stack + doorGap);
  const doors = doorBank({
    left,
    spanWidth,
    centerY: doorTop - height / 2,
    height,
    handleAtBottom,
    singleHandleOnRight: true,
    // "1 Door & 2 Drawer" (the catalog): one door at its width
    count: 1,
    firstIndex: 0,
  });
  return {
    fronts: [...doors, ...drawers].map((f) => ({ ...f, column: 0 })),
    spans: [
      { index: 0, type: column.type, left: -width / 2, right: width / 2 },
    ],
  };
}

/**
 * The boards of an END SHELF (`endShelves.js`, group 3e) as seen from the
 * front: an open shelf's, with no side on its OPEN side (`side`, 'left' /
 * 'right' as seen facing it) — the top, bottom and shelves run out to that
 * edge. With no single open side, an open shelf's boards.
 */
function endShelfBoards({ width, bodyHeight, bodyCenterY, side }) {
  const boards = openShelfBoards({ width, bodyHeight, bodyCenterY });
  if (side !== 'left' && side !== 'right') return boards;
  const t = CABINET.panelThick;
  const o = side === 'right' ? 1 : -1;
  return boards
    .filter((b) => b.name !== `shelf-side-${side}`)
    .map((b) =>
      b.name.startsWith('shelf-side')
        ? b
        : { ...b, x: b.x + (o * t) / 2, width: b.width + t }
    );
}

/**
 * An ANGLED END (`angledEnds.js`, group 6): the cut, the carcass's plan
 * outline behind its doors and the toe kick's (a base's), all in the box's
 * own frame, and its doors — full height, one on the straight front (a
 * base's 12") and one on the angled face. Each door is placed twice: `x` /
 * `width` as the elevation sees it face-on (the angled one foreshortened),
 * and `placement` — its true `width`, its centre in plan and its turn about Y
 * — for the 3D view. Null for a box with no open side (drawn square).
 */
function angledEndFronts({
  width,
  depth,
  height,
  isUpperCabinet,
  bodyCenterY,
  doorHeight,
  handleAtBottom,
  side,
}) {
  const probe = {
    category: isUpperCabinet
      ? { 'upper-cabinet': true, angledEnd: true }
      : { 'lower-cabinet': true, angledEnd: true },
    dimensions: { x: width, y: height, z: depth },
    angledEndSide: side,
  };
  const cut = angledEndCut(probe);
  if (!cut) return null;
  const { panelThick: t, doorReveal: r, toeKickSetback } = CABINET;
  const s = side === 'right' ? 1 : -1;
  const door = (name, x, w, faceW, placement, handleOnRight) => ({
    name,
    kind: 'door',
    x,
    y: bodyCenterY,
    width: faceW,
    height: doorHeight,
    styled: true,
    isDrawer: false,
    column: 0,
    placement: { ...placement, width: w },
    hardware: {
      name: `handle-${name.slice(5)}`,
      isDrawer: false,
      handleOnRight,
      handleAtBottom,
    },
  });
  const fronts = [];
  // the straight front: from the closed side to where the angle starts
  const straight = width - cut.cutM;
  if (straight > 2 * r + 0.01) {
    const x0 = -s * (width / 2);
    const x1 = cut.front.x;
    const cx = (x0 + x1) / 2;
    const w = straight - 2 * r;
    fronts.push(
      door('door-0', cx, w, w, { x: cx, z: depth / 2 - t / 2, rotY: 0 }, s > 0)
    );
  }
  // the angled face: its door's handle toward the straight front
  const length = cut.cutM * Math.SQRT2;
  const w = length - 2 * r;
  const mid = {
    x: (cut.front.x + cut.end.x) / 2,
    z: (cut.front.z + cut.end.z) / 2,
  };
  fronts.push(
    door(
      `door-${fronts.length}`,
      mid.x,
      w,
      w * Math.SQRT1_2,
      {
        x: mid.x - (cut.normal.x * t) / 2,
        z: mid.z - (cut.normal.z * t) / 2,
        rotY: (s * Math.PI) / 4,
      },
      s < 0
    )
  );
  return {
    angledEnd: {
      side,
      cutM: cut.cutM,
      outline: angledEndLocalOutline(probe),
      carcassOutline: angledEndInset(probe, t),
      toeKickOutline: isUpperCabinet
        ? null
        : angledEndInset(probe, toeKickSetback),
    },
    fronts,
  };
}

/**
 * Shelves INSIDE an open-shelf box: as many as keep its compartments near
 * `CABINET.openShelfSpacing` (12") — a 30" box carries two, a 15" bridge none.
 */
function cabinetOpenShelfCount(bodyHeight) {
  return Math.max(0, Math.round(bodyHeight / CABINET.openShelfSpacing) - 1);
}

/**
 * The boards of an OPEN SHELF as seen from the front, in the layout's centred
 * metres: the two sides full height, the top and bottom between them, and the
 * shelves dividing the clear height evenly. Every surface that draws the box
 * draws THESE — the 3D renderer as solids the box's depth, the schematic and
 * the thumbnail as rectangles.
 */
function openShelfBoards({ width, bodyHeight, bodyCenterY }) {
  const t = CABINET.panelThick;
  const inner = width - 2 * t;
  const clear = bodyHeight - 2 * t;
  const board = (name, x, y, w, h) => ({ name, x, y, width: w, height: h });
  const boards = [
    board('shelf-side-left', -width / 2 + t / 2, bodyCenterY, t, bodyHeight),
    board('shelf-side-right', width / 2 - t / 2, bodyCenterY, t, bodyHeight),
    board('shelf-top', 0, bodyCenterY + bodyHeight / 2 - t / 2, inner, t),
    board('shelf-bottom', 0, bodyCenterY - bodyHeight / 2 + t / 2, inner, t),
  ];
  const count = cabinetOpenShelfCount(bodyHeight);
  for (let i = 1; i <= count; i += 1) {
    const y = bodyCenterY - clear / 2 + (clear * i) / (count + 1);
    boards.push(board(`shelf-board-${i - 1}`, 0, y, inner, t));
  }
  return boards;
}

/**
 * FLOATING SHELVES: boards only, no box (`floatingShelves.js`) — the full
 * width, each `FLOATING_SHELF.thicknessM` thick, the first on the bay's
 * bottom (the uppers' bottom line, a shelf's usual height over a counter) and
 * one every `spacingM` up while it stays under the uppers' top — never one up
 * at the cabinet tops, out of reach (the user, 2026-10-31). As many as
 * `floatingShelfCount` says.
 */
function floatingShelfBoards({ width, bodyHeight, bodyCenterY }) {
  const { thicknessM: t, spacingM } = FLOATING_SHELF;
  const bottom = bodyCenterY - bodyHeight / 2;
  return Array.from({ length: floatingShelfCount(bodyHeight) }, (_, i) => ({
    name: `shelf-board-${i}`,
    x: 0,
    y: bottom + t / 2 + i * spacingM,
    width,
    height: t,
  }));
}

/**
 * Doors across a span: one up to 12", a pair up to 48", then an even count of
 * ≤18" doors.
 */
function cabinetDoorCount(spanWidth) {
  const { maxSingleDoorWidth, maxTwoDoorWidth, maxDoorWidth } = CABINET;
  if (spanWidth <= maxSingleDoorWidth) return 1;
  if (spanWidth <= maxTwoDoorWidth) return 2;
  const raw = Math.ceil(spanWidth / maxDoorWidth);
  return Math.max(2, raw + (raw % 2));
}

/** Drawers in a stack: the column's own count, else ~8" drawers, at least 2. */
function cabinetDrawerCount(column, doorHeight) {
  if (column.drawerCount > 0) return column.drawerCount;
  return Math.max(
    CABINET.minDrawerCount,
    Math.round(doorHeight / CABINET.maxDrawerHeight)
  );
}

/**
 * The height of the TOP front on a base column, whatever that front happens
 * to be: the first drawer of a `drawers` stack, the working drawer of a
 * `drawer-doors` column, the fixed apron of a `sink-front`, or the false rail
 * over plain doors.
 *
 * ONE number, because these columns stand SIDE BY SIDE at the same door
 * height — as the bays of a cabinet run (`bayOverrides` maps a run's Drawer /
 * Drawer+Doors / Sink bays onto exactly these three types) and as the columns
 * of a single vanity (`generateCabinetColumns` puts a `doors` column under
 * each basin and `drawers` either side of it). A top front derived per type
 * makes that seam step: a 32" double vanity drew a 9.07" top drawer next to a
 * 6.72" rail, and a run of 34.5" bases drew 7.35" / 8.49" / 7.09" across
 * three neighbours. Deriving all four from the default drawer stack lines the
 * whole face up at any cabinet height.
 *
 * `farmhouse-front` is deliberately NOT one of these — its drop is the height
 * of the sink's own apron standing in that plane, a real object's dimension
 * rather than a rhythm of the cabinet face.
 */
function cabinetTopFrontHeight(doorHeight) {
  const count = cabinetDrawerCount({}, doorHeight);
  return (doorHeight - CABINET.doorGap * (count - 1)) / count;
}

/**
 * The strip a doors-type column carries above its doors. `kind: null` means
 * nothing is drawn but the doors still drop by `height` (farmhouse).
 */
function topStripFor(column, columnIndex, doorHeight) {
  const c = CABINET;
  switch (column.type) {
    case 'farmhouse-front':
      return {
        kind: null,
        height: Math.max(
          0,
          (column.apronDrop ?? c.farmhouseApronDrop) - c.doorReveal
        ),
      };
    case 'sink-front':
      return {
        kind: 'apron',
        name: `sink-apron-${columnIndex}`,
        height: cabinetTopFrontHeight(doorHeight),
        isDrawer: false,
        hardwareName: null,
      };
    case 'drawer-doors':
      return {
        kind: 'drawer',
        name: `drawer-doors-top-${columnIndex}`,
        height: cabinetTopFrontHeight(doorHeight),
        isDrawer: true,
        hardwareName: `drawer-doors-handle-${columnIndex}`,
      };
    default:
      return {
        kind: 'rail',
        name: `door-rail-${columnIndex}`,
        height: cabinetTopFrontHeight(doorHeight),
        isDrawer: true,
        hardwareName: null,
      };
  }
}

function drawerHardware(name) {
  return { name, isDrawer: true, handleOnRight: false, handleAtBottom: false };
}

/** A bank of `count` doors filling `spanWidth` from `left`, gaps between. */
function doorBank({
  left,
  spanWidth,
  centerY,
  height,
  handleAtBottom,
  singleHandleOnRight,
  firstIndex,
  // How many doors, when the column says (a blind corner cabinet's reachable
  // part is ONE door at any width, like the lower tier's blind unit); else the
  // width decides.
  count = cabinetDoorCount(spanWidth),
}) {
  const { doorGap } = CABINET;
  const doorWidth = (spanWidth - doorGap * (count - 1)) / count;
  const fronts = [];
  for (let di = 0; di < count; di++) {
    const index = firstIndex + di;
    fronts.push({
      name: `door-${index}`,
      kind: 'door',
      x: left + doorWidth / 2 + di * (doorWidth + doorGap),
      y: centerY,
      width: doorWidth,
      height,
      styled: true,
      isDrawer: false,
      hardware: {
        name: `handle-${index}`,
        isDrawer: false,
        // A lone door pulls toward the cabinet centre; a bank alternates so
        // neighbouring doors pull toward each other.
        handleOnRight: count === 1 ? singleHandleOnRight : di % 2 === 0,
        handleAtBottom,
      },
    });
  }
  return fronts;
}

/**
 * Each drawer's height in a stack, top first. Equal, or — `graduated` — the
 * top one the shared top front (`cabinetTopFrontHeight`, so it lines up with
 * the bays beside it) and the rest sharing what is left.
 */
function cabinetDrawerHeights(column, doorHeight) {
  const { doorGap } = CABINET;
  const count = cabinetDrawerCount(column, doorHeight);
  const open = doorHeight - doorGap * (count - 1);
  if (!column.graduated || count < 2) return Array(count).fill(open / count);
  const first = cabinetTopFrontHeight(doorHeight);
  return [first, ...Array(count - 1).fill((open - first) / (count - 1))];
}

function drawerStack({ column, columnIndex, left, colWidth, top, doorHeight }) {
  const { doorGap } = CABINET;
  const heights = cabinetDrawerHeights(column, doorHeight);
  const [first] = heights;
  const rest = heights[heights.length - 1];
  // How far drawer `di`'s top edge sits below the stack's: the first drawer,
  // then equal ones. An equal stack keeps its old arithmetic, so its
  // coordinates are exactly what they were before stacks could be graduated.
  const drop = (di) =>
    first === rest
      ? di * (rest + doorGap)
      : di && first + doorGap + (di - 1) * (rest + doorGap);
  const fronts = [];
  for (let di = 0; di < heights.length; di++) {
    const drawerHeight = heights[di];
    fronts.push({
      name: `drawer-${columnIndex}-${di}`,
      kind: 'drawer',
      x: left + colWidth / 2,
      y: top - drawerHeight / 2 - drop(di),
      width: colWidth,
      height: drawerHeight,
      styled: true,
      isDrawer: true,
      hardware: drawerHardware(`drawer-handle-${columnIndex}-${di}`),
    });
  }
  return fronts;
}

function doorsColumn({
  column,
  columnIndex,
  left,
  colWidth,
  bodyCenterY,
  doorHeight,
  handleAtBottom,
  firstDoorIndex,
}) {
  const { doorGap } = CABINET;
  if (column.type === 'full-height-doors') {
    // No strip and no gap beneath one: the doors are the whole column.
    return doorBank({
      left,
      spanWidth: colWidth,
      centerY: bodyCenterY,
      height: doorHeight,
      handleAtBottom,
      singleHandleOnRight: column.handleOnRight ?? left + colWidth / 2 < 0,
      firstIndex: firstDoorIndex,
      ...(column.doors > 0 ? { count: column.doors } : {}),
    });
  }
  const strip = topStripFor(column, columnIndex, doorHeight);
  const fronts = [];
  const stripY = bodyCenterY + doorHeight / 2 - strip.height / 2;
  if (strip.kind && column.sideDrawerWidth > 0) {
    fronts.push(
      ...sinkRowWithSideDrawers({
        column,
        columnIndex,
        left,
        colWidth,
        strip,
      }).map((f) => ({ ...f, y: stripY, height: strip.height }))
    );
  } else if (strip.kind) {
    fronts.push({
      name: strip.name,
      kind: strip.kind,
      x: left + colWidth / 2,
      y: stripY,
      width: colWidth,
      height: strip.height,
      styled: true,
      isDrawer: strip.isDrawer,
      hardware: strip.hardwareName ? drawerHardware(strip.hardwareName) : null,
    });
  }
  if (column.type === 'farmhouse-front') {
    fronts.push(
      ...farmhouseBands({
        column,
        columnIndex,
        left,
        colWidth,
        y: bodyCenterY + doorHeight / 2 - strip.height / 2,
        height: strip.height,
      })
    );
  }
  fronts.push(
    ...doorBank({
      left,
      spanWidth: colWidth,
      centerY: bodyCenterY - strip.height / 2 - doorGap / 2,
      height: doorHeight - strip.height - doorGap,
      handleAtBottom,
      singleHandleOnRight: left + colWidth / 2 < 0,
      firstIndex: firstDoorIndex,
      // the part's own door count where it says (Venus's 30" sink section
      // is ONE door, 17½" wide) — else the width decides
      ...(column.doors > 0 ? { count: column.doors } : {}),
    })
  );
  return fronts;
}

/**
 * A sink section whose top row is a working drawer each side of the false
 * front (`sideDrawerWidth` each, in the column's own metres), the doors
 * running under all three — Venus's 42" VSD4221. The row's x-extents only;
 * the caller sets its height and line.
 */
function sinkRowWithSideDrawers({
  column,
  columnIndex,
  left,
  colWidth,
  strip,
}) {
  const { doorGap } = CABINET;
  const side = Math.min(
    (column.sideDrawerWidth * colWidth) / column.width,
    (colWidth - 2 * doorGap) / 3
  );
  const drawer = (k, x) => ({
    name: `sink-side-drawer-${columnIndex}-${k}`,
    kind: 'drawer',
    x,
    width: side - doorGap / 2,
    styled: true,
    isDrawer: true,
    hardware: drawerHardware(`sink-side-drawer-handle-${columnIndex}-${k}`),
  });
  const right = left + colWidth;
  return [
    drawer(0, left + (side - doorGap / 2) / 2),
    {
      name: strip.name,
      kind: strip.kind,
      x: left + colWidth / 2,
      width: colWidth - 2 * side - doorGap,
      styled: true,
      isDrawer: strip.isDrawer,
      hardware: null,
    },
    drawer(1, right - (side - doorGap / 2) / 2),
  ];
}

function fillerFront(centerX, fillerWidth, bodyCenterY, doorHeight) {
  const panelWidth = fillerWidth - CABINET.doorReveal;
  if (panelWidth < CABINET.fillerMin) return [];
  return [
    {
      name: 'filler-panel',
      kind: 'filler',
      x: centerX,
      y: bodyCenterY,
      width: panelWidth,
      height: doorHeight,
      styled: false,
      isDrawer: false,
      hardware: null,
    },
  ];
}

/** Remainder strips when the columns don't fill the width, per `remainderAlign`. */
function fillerFronts({
  width,
  fillerWidth,
  remainderAlign,
  bodyCenterY,
  doorHeight,
}) {
  const { doorReveal } = CABINET;
  const filler = (cx, fw) => fillerFront(cx, fw, bodyCenterY, doorHeight);
  if (remainderAlign === 'left') {
    return filler(-width / 2 + doorReveal + fillerWidth / 2, fillerWidth);
  }
  if (remainderAlign === 'right') {
    return filler(width / 2 - doorReveal - fillerWidth / 2, fillerWidth);
  }
  const half = fillerWidth / 2;
  return [
    ...filler(-width / 2 + doorReveal + half / 2, half),
    ...filler(width / 2 - doorReveal - half / 2, half),
  ];
}

/**
 * Columns are laid out from `-width / 2` with no bound of their own, so a set
 * that sums WIDER than the box would draw its fronts straight out of the
 * carcass, all of the overhang on the far end. `generateCabinetColumns` no
 * longer emits such a set, but rooms processed before that fix carry one in
 * Firestore until they are reprocessed (haines Scan 1 bath B88124DF: 3.76" of
 * door past the box). Scale to fit so what renders is the cabinet that is
 * actually there.
 */
function clipColumnsToWidth(columns, width) {
  const rawTotal = columns.reduce((sum, c) => sum + c.width, 0);
  if (!(rawTotal > width && rawTotal > 0)) return columns;
  const scale = width / rawTotal;
  return columns.map((c) => ({ ...c, width: c.width * scale }));
}

/**
 * A farmhouse column's band is the sink's own apron — but the apron is only as
 * wide as the basin, so a 30" sink in a 36" base leaves 3" of open box either
 * side of it. Close whatever the apron does not cover with a flat panel (a
 * stile, so no handle). Without `apronWidth` the whole band is assumed
 * covered, which is how every farmhouse column built before this read.
 */
function farmhouseBands({ column, columnIndex, left, colWidth, y, height }) {
  if (column.apronWidth == null || height <= 0) return [];
  const apronHalf = Math.min(column.apronWidth, colWidth) / 2;
  const apronMid = left + colWidth / 2 + (column.apronOffset ?? 0);
  const spans = [
    [left, apronMid - apronHalf],
    [apronMid + apronHalf, left + colWidth],
  ];
  const fronts = [];
  for (const [lo, hi] of spans) {
    if (hi - lo <= CABINET.farmhouseBandMin) continue;
    fronts.push({
      name: `farmhouse-band-${columnIndex}-${fronts.length}`,
      kind: 'band',
      x: (lo + hi) / 2,
      y,
      width: hi - lo,
      height,
      styled: false,
      isDrawer: false,
      hardware: null,
    });
  }
  return fronts;
}

function columnFronts({
  columns,
  width,
  remainderAlign,
  bodyCenterY,
  doorHeight,
  handleAtBottom,
}) {
  const { doorReveal, doorGap, fillerMin } = CABINET;
  columns = clipColumnsToWidth(columns, width);
  const columnsTotal = columns.reduce((sum, c) => sum + c.width, 0);
  const fillerWidth = width - columnsTotal;
  const hasFiller = fillerWidth > fillerMin;
  const columnOffset = !hasFiller
    ? 0
    : remainderAlign === 'left'
      ? fillerWidth
      : remainderAlign === 'right'
        ? 0
        : fillerWidth / 2;

  const fronts = [];
  const spans = [];
  let cursor = -width / 2 + doorReveal + columnOffset;
  let doorIndex = 0;

  columns.forEach((column, ci) => {
    const isFirst = ci === 0;
    const isLast = ci === columns.length - 1;
    const colWidth =
      column.width -
      (isFirst ? doorReveal : doorGap / 2) -
      (isLast ? doorReveal : doorGap / 2);
    const firstFront = fronts.length;
    spans.push({
      index: ci,
      type: column.type,
      left: cursor - (isFirst ? doorReveal : doorGap / 2),
      right: cursor + colWidth + (isLast ? doorReveal : doorGap / 2),
      // what slides out is a rack, not a drawer box (the spice pull-out)
      ...(column.rack ? { rack: column.rack } : {}),
    });

    if (column.type === 'false-panel') {
      fronts.push({
        name: `false-panel-${ci}`,
        kind: 'false-panel',
        x: cursor + colWidth / 2,
        y: bodyCenterY,
        width: colWidth,
        height: doorHeight,
        styled: false,
        isDrawer: false,
        hardware: null,
      });
    } else if (column.type === 'drawers') {
      fronts.push(
        ...drawerStack({
          column,
          columnIndex: ci,
          left: cursor,
          colWidth,
          top: bodyCenterY + doorHeight / 2,
          doorHeight,
        })
      );
    } else {
      const columnDoors = doorsColumn({
        column,
        columnIndex: ci,
        left: cursor,
        colWidth,
        bodyCenterY,
        doorHeight,
        handleAtBottom,
        firstDoorIndex: doorIndex,
      });
      doorIndex += columnDoors.filter((f) => f.kind === 'door').length;
      fronts.push(...columnDoors);
    }
    for (let i = firstFront; i < fronts.length; i++) fronts[i].column = ci;

    cursor += colWidth + (isLast ? 0 : doorGap);
  });

  if (hasFiller) {
    fronts.push(
      ...fillerFronts({
        width,
        fillerWidth,
        remainderAlign,
        bodyCenterY,
        doorHeight,
      }).map((f) => ({ ...f, column: null }))
    );
  }
  return { fronts, spans };
}

/**
 * The under-cabinet LED strip's box under a flagged upper, or `null` when the
 * cabinet carries none: not an upper, the over-fridge upper (the strip light
 * skips it too — `underCabinetStrips`), unflagged, or too narrow to hold the
 * channel.
 *
 * It hangs directly below the carcass bottom, its centre line
 * `STRIP_FRONT_INSET_M` behind the door face and each end `STRIP_END_INSET_M`
 * short of the side — the same two insets `underCabinetStrips` places the
 * strip LIGHT with, so the glowing channel and the light it stands for sit on
 * one line. `{ x, y, z }` is the box centre, `length` runs along x.
 */
function cabinetLightStrip(
  { width, depth, bodyCenterY, bodyHeight, isUpperCabinet, isOverFridge },
  underCabinetLight
) {
  if (!underCabinetLight || !isUpperCabinet || isOverFridge) return null;
  const length = width - 2 * STRIP_END_INSET_M;
  if (!(length > 0)) return null;
  const { lightStripDepth: stripDepth, lightStripHeight: height } = CABINET;
  return {
    x: 0,
    y: bodyCenterY - bodyHeight / 2 - height / 2,
    z: depth / 2 - STRIP_FRONT_INSET_M,
    length,
    height,
    depth: stripDepth,
  };
}

/**
 * Lay out a cabinet front.
 *
 * @param {object} dimensions `{ width, height, depth, handleAtBottom? }` —
 *   `handleAtBottom` rides on `dimensions` because that is where the kitchen
 *   materializer / cabinet runs stamp it for an over-fridge upper.
 * @param {object} options `{ isUpperCabinet, isOverFridge, columns,
 *   remainderAlign, underCabinetLight }`
 */
function cabinetFrontLayout(dimensions, options = {}) {
  const { width, height, depth } = dimensions;
  const { panelThick, doorReveal, toeKickHeight, toeKickSetback } = CABINET;
  const isUpperCabinet = !!options.isUpperCabinet;
  const isOverFridge = !!options.isOverFridge;
  // Uppers keep their pulls within reach from standing height.
  const handleAtBottom = isUpperCabinet || dimensions.handleAtBottom === true;

  const bodyHeight = isUpperCabinet ? height : height - toeKickHeight;
  const bodyCenterY = isUpperCabinet ? 0 : toeKickHeight / 2;
  const boxDepth = depth - panelThick;
  const boxCenterZ = -panelThick / 2;
  const doorHeight = bodyHeight - doorReveal * 2;
  const doorZ = depth / 2 - panelThick / 2;

  const toeKick = isUpperCabinet
    ? null
    : {
        height: toeKickHeight,
        depth: boxDepth - toeKickSetback,
        centerY: -height / 2 + toeKickHeight / 2,
        centerZ: boxCenterZ - toeKickSetback / 2,
      };

  const columns =
    Array.isArray(options.columns) && options.columns.length > 0
      ? options.columns
      : null;
  // The whole box, or not at all: an open shelf has no door plane to share
  // with a door column beside it. Floating shelves are its boards with no box
  // round them (`floating`).
  const isFloating =
    !!columns && columns.every((c) => c.type === 'floating-shelves');
  // A valance bay is no box and no boards: the valance hung in its gap is the
  // trim's to draw (`valanceBays.js`)
  const isValance = !!columns && columns.every((c) => c.type === 'valance');
  // An end shelf is an open shelf with no side on its open side (`endShelf`)
  const isEndShelf = !!columns && columns.every((c) => c.type === 'end-shelf');
  // A rack is the open shelf's shell holding its pattern (`racks.js`)
  const rackId =
    columns?.length === 1 && RACKS[columns[0].type] ? columns[0].type : null;
  const isOpenShelf =
    isFloating ||
    isValance ||
    isEndShelf ||
    !!rackId ||
    (!!columns && columns.every((c) => c.type === 'open-shelf'));
  const endSide = options.endShelfSide ?? null;
  let openShelf = null;
  if (isValance) openShelf = { boards: [], floating: true, valance: true };
  else if (isFloating)
    openShelf = {
      boards: floatingShelfBoards({ width, bodyHeight, bodyCenterY }),
      floating: true,
    };
  else if (isEndShelf)
    openShelf = {
      boards: endShelfBoards({ width, bodyHeight, bodyCenterY, side: endSide }),
      endShelf: { side: endSide },
    };
  else if (rackId) {
    const held = rackPieces(rackId, { width, bodyHeight, bodyCenterY, depth });
    openShelf = {
      // the shell's sides, top and bottom — no shelves — and what it holds
      boards: [
        ...openShelfBoards({ width, bodyHeight, bodyCenterY }).filter(
          (b) => !b.name.startsWith('shelf-board')
        ),
        ...held.boards,
      ],
      rack: { type: rackId, pattern: RACKS[rackId].pattern, bars: held.bars },
    };
  } else if (isOpenShelf)
    openShelf = { boards: openShelfBoards({ width, bodyHeight, bodyCenterY }) };
  // A microwave cabinet: its opening over a drawer, or under doors
  const microwaveFront =
    columns?.length === 1 && MICROWAVE_CABINETS[columns[0].type]
      ? microwaveFronts({
          frontId: columns[0].type,
          width,
          bodyCenterY,
          doorHeight,
          handleAtBottom,
        })
      : null;
  // …or an angled end's doors on its faces (`angledEnds.js`)
  const angled =
    columns?.length === 1 && columns[0].type === 'angled-end'
      ? angledEndFronts({
          width,
          depth,
          height,
          isUpperCabinet,
          bodyCenterY,
          doorHeight,
          handleAtBottom,
          side: options.angledEndSide ?? null,
        })
      : null;
  // …or a door over drawers (the wall cabinet with drawers)
  const doorOverDrawers =
    columns?.length === 1 && columns[0].type === 'door-over-drawers'
      ? doorOverDrawerFronts({
          column: columns[0],
          width,
          bodyCenterY,
          doorHeight,
          handleAtBottom,
        })
      : null;
  const { fronts, spans } = isOpenShelf
    ? { fronts: [], spans: [] }
    : microwaveFront ||
      (angled && { fronts: angled.fronts, spans: [] }) ||
      doorOverDrawers ||
      (columns
        ? columnFronts({
            columns,
            width,
            remainderAlign: options.remainderAlign || 'center',
            bodyCenterY,
            doorHeight,
            handleAtBottom,
          })
        : {
            fronts: doorBank({
              left: -width / 2 + doorReveal,
              spanWidth: width - doorReveal * 2,
              centerY: bodyCenterY,
              height: doorHeight,
              handleAtBottom,
              singleHandleOnRight: true,
              firstIndex: 0,
            }).map((f) => ({ ...f, column: 0 })),
            spans: [
              { index: 0, type: null, left: -width / 2, right: width / 2 },
            ],
          });

  return {
    width,
    height,
    depth,
    isUpperCabinet,
    isOverFridge,
    bodyHeight,
    bodyCenterY,
    boxDepth,
    boxCenterZ,
    doorHeight,
    doorThickness: panelThick,
    doorZ,
    toeKick,
    fronts,
    // Each column's slice of the carcass, `left`..`right` in x (the boundary
    // between two columns is the middle of the gap between their fronts).
    // Every front but a filler carries the `column` index it belongs to.
    // Read by `cabinetCompartments.js`; empty for an open shelf.
    columns: spans,
    openShelf,
    // a microwave cabinet's opening and shelf, or null
    microwave: microwaveFront?.microwave ?? null,
    // an angled end's cut, carcass and toe-kick outlines, or null
    angledEnd: angled?.angledEnd ?? null,
    // the glass holder's rails under an upper (`glassHolders.js`), the
    // holder's depth front to back — or null
    glassHolder:
      isUpperCabinet && options.glassHolder === true
        ? {
            rails: glassHolderRails(width, bodyCenterY - bodyHeight / 2),
            depth: GLASS_HOLDER.depthIn * 0.0254,
          }
        : null,
    // The LED strip under a flagged upper (`cabinetLightStrip`), or null.
    lightStrip: cabinetLightStrip(
      {
        width,
        depth,
        bodyCenterY,
        bodyHeight,
        isUpperCabinet,
        isOverFridge,
      },
      options.underCabinetLight === true
    ),
  };
}

/**
 * Where one front's hardware sits and what shape it is, in the same centred
 * coordinates. `null` for handleless. Cup pulls exist only on drawers; a
 * cup-pull door gets a knob.
 *
 * @returns {null | {kind:'bar', x, y, orientation:'vertical'|'horizontal', length, diameter}
 *           | {kind:'knob', x, y, radius}
 *           | {kind:'cup', x, y, length, height, projection}}
 */
function cabinetHardwarePlacement(front, hardwareType) {
  const h = CABINET_HARDWARE;
  const type = resolveHardwareType(hardwareType);
  if (type === 'handleless' || !front.hardware) return null;
  const { isDrawer, handleOnRight, handleAtBottom } = front.hardware;

  if (isDrawer) {
    if (type === 'cup-pull') {
      return {
        kind: 'cup',
        x: front.x,
        y: front.y,
        length: Math.min(h.cupLength, front.width * h.cupLengthFrac),
        height: Math.min(h.cupHeight, front.height * h.cupHeightFrac),
        projection: h.cupProjection,
      };
    }
    return {
      kind: 'bar',
      x: front.x,
      y: front.y,
      orientation: 'horizontal',
      length: h.barLength,
      diameter: h.barDiameter,
    };
  }

  const x = handleOnRight
    ? front.x + front.width / 2 - h.edgeInset
    : front.x - front.width / 2 + h.edgeInset;
  const bottom = front.y - front.height / 2;

  if (type === 'knob' || type === 'cup-pull') {
    return {
      kind: 'knob',
      x,
      y: handleAtBottom ? bottom + h.knobRadius + h.knobBottomOffset : front.y,
      radius: h.knobRadius,
    };
  }
  return {
    kind: 'bar',
    x,
    y: handleAtBottom ? bottom + h.barLength / 2 + h.barBottomOffset : front.y,
    orientation: 'vertical',
    length: h.barLength,
    diameter: h.barDiameter,
  };
}

/** How far the hardware stands proud of the door face (its full depth). */
function cabinetHardwareProjection(placement) {
  const h = CABINET_HARDWARE;
  if (!placement) return 0;
  if (placement.kind === 'cup') return h.frontGap + placement.projection;
  if (placement.kind === 'knob') return h.frontGap + placement.radius * 2;
  return h.frontGap + placement.diameter;
}

module.exports = {
  CABINET_DOOR_STYLES,
  CABINET_HARDWARE_TYPES,
  DEFAULT_DOOR_STYLE,
  DEFAULT_HARDWARE_TYPE,
  resolveDoorStyle,
  frontDoorStyle,
  cornerDoorStyle,
  resolveHardwareType,
  cabinetFrameRailWidth,
  cabinetOpenShelfCount,
  cabinetDoorCount,
  cabinetDrawerCount,
  cabinetTopFrontHeight,
  cabinetDrawerHeights,
  cabinetLightStrip,
  cabinetFrontLayout,
  cabinetHardwarePlacement,
  cabinetHardwareProjection,
};
